/**
 * Pure, backend-formula-mirroring math for the interactive Payoff
 * Visualizer & Strike Manipulation Engine (Iron Condor anchor strategy).
 *
 * Nothing in this file makes a network call or touches React state -- it
 * exists so `PayoffChart.tsx`'s drag interaction can redraw the solid
 * "Expiry P&L" line on every mouse-move with ZERO backend round-trip: an
 * at-expiry option payoff is pure intrinsic value (no volatility/time-value
 * component at all), so it can be computed exactly, client-side, from the
 * same `legs` the Trade Builder already holds. The (genuinely
 * model-dependent) T+0 Black-Scholes line and every backend-computed metric
 * (margin, probability of profit, aggregate Greeks) are deliberately NOT
 * duplicated here -- those stay backend-authoritative via the existing
 * `strategyAdjustmentService.recalculate()`/`payoffCurve()` debounced
 * refresh, satisfying "debounce heavy Black-Scholes calculations during
 * active dragging" by construction rather than by a special-cased timer.
 *
 * `payoffAtPrice()`/`computeNetPremium()` intentionally mirror backend
 * `app/services/strategy_adjustment/metrics_calculator.py`'s
 * `_payoff_at_price()`/`_net_premium()` formulas line-for-line (same sign
 * conventions: a SELL leg's premium is a credit (+), a BUY leg's is a debit
 * (-); intrinsic value is `max(0, price-strike)` for a call and
 * `max(0, strike-price)` for a put) -- given the same leg premiums, this
 * file's output is bit-for-bit consistent with what the backend will
 * eventually confirm, not merely a visual approximation.
 */
import type { OptionLeg } from "@/types";

// ---- Expiry payoff curve (mirrors metrics_calculator.py) ------------------

/** Mirrors backend `_GRID_UPPER_FACTOR` / `_CURVE_GRID_POINTS` in
 * `metrics_calculator.build_payoff_curve()` -- keeping the same grid shape
 * means the locally-generated drag-preview curve and the backend's own
 * `/payoff-curve` response share the same x-axis domain, so nothing visibly
 * "jumps" when the authoritative curve replaces the local one after the
 * debounced refresh lands. */
export const PAYOFF_GRID_UPPER_FACTOR = 1.6;
export const PAYOFF_GRID_POINTS = 80;

export interface LocalPayoffPoint {
  underlying_price: number;
  expiry_pnl: number;
}

function legSign(leg: OptionLeg): number {
  return leg.side === "BUY" ? 1 : -1;
}

/** Coerces a value that may arrive as a genuine JS `number` (a
 * locally-edited leg, e.g. via `TextField`'s own `Number(e.target.value)`
 * in `TradeBuilderCard`) OR as a numeric STRING (an as-yet-untouched leg
 * straight off the wire). The backend's `strike`/`premium`/`spot`/
 * `breakeven_points`/etc. are Python `Decimal` fields, and Pydantic v2
 * serializes `Decimal` to a JSON STRING (e.g. `"23550.00"`), not a bare
 * JSON number -- confirmed against `app/schemas/strategy_adjustment.py`
 * and the `pydantic==2.10.4` default behavior, with no custom encoder
 * anywhere overriding it. The frontend's `OptionLeg`/`OriginalRecommendationRead`
 * types declare these fields as `number` anyway (a known, pre-existing,
 * deliberate convention -- see `OptionLeg`'s own doc comment in `@/types`)
 * because ordinary arithmetic operators (`+ - * /`, but NOT `Number.
 * isFinite`, `===`, or `Set` membership) auto-coerce a numeric string, so
 * the rest of the app has always tolerated this silently. Every place in
 * THIS file that validates a value's finiteness, dedupes it via `Set`, or
 * otherwise needs a real `number` rather than relying on implicit
 * coercion goes through this first, since `Number.isFinite("23550")` is
 * `false` -- the exact regression that produced an axis-blowing-out bug
 * indistinguishable from the actual bug this same check was meant to
 * prevent (a leg with a genuinely unset/invalid strike). Returns `NaN` for
 * anything that isn't a number or a numeric string, which every caller
 * here already treats as "not a real value" via `Number.isFinite`. */
function toNum(value: unknown): number {
  return typeof value === "number" ? value : Number(value);
}

function intrinsicValue(leg: OptionLeg, price: number): number {
  const strike = toNum(leg.strike);
  return leg.option_type === "CE" ? Math.max(0, price - strike) : Math.max(0, strike - price);
}

/** A leg fresh off `TradeBuilderCard`'s "Add Leg" button starts at
 * `strike: 0` before the trader fills it in (and clearing the strike input
 * to blank produces `Number("") === 0` too) -- a real, easily-reached state
 * of `legs` while actively editing, not a hypothetical. A CE leg at
 * strike 0 has intrinsic value `max(0, price - 0) = price` at every price
 * -- an unbounded synthetic long position -- so including it in P&L math
 * doesn't just look odd, it silently wrecks max profit/loss and every
 * breakeven for the WHOLE strategy while it's present. Treated the same
 * way an unpriced leg's `premium` already is (contributes nothing until
 * it's a real leg), not as a validation error the trader has to see.
 * Coerces via `toNum` first (see that function) so a perfectly valid,
 * untouched leg straight off the wire (`strike` as the STRING "23550.00")
 * isn't itself misclassified as invalid. */
function isValidLeg(leg: OptionLeg): boolean {
  const strike = toNum(leg.strike);
  return Number.isFinite(strike) && strike > 0;
}

/** Positive = net credit received, negative = net debit paid -- identical
 * convention to backend `_net_premium()`. A leg with no known `premium` yet
 * (not observed, not yet theoretically priced) contributes 0, same as
 * treating an unpriced leg as free until the backend's own pricing engine
 * fills it in on the next debounced refresh. An unset/invalid-strike leg
 * (see `isValidLeg`) contributes nothing at all. */
export function computeNetPremium(legs: OptionLeg[]): number {
  let total = 0;
  for (const leg of legs) {
    if (!isValidLeg(leg)) continue;
    const premium = toNum(leg.premium ?? 0);
    const lotSize = toNum(leg.lot_size);
    const quantity = toNum(leg.quantity);
    const creditSign = leg.side === "BUY" ? -1 : 1;
    total += creditSign * premium * lotSize * quantity;
  }
  return total;
}

/** Exact expiry P&L for `legs` at one underlying `price` -- mirrors backend
 * `_payoff_at_price()` exactly. Ignores unset/invalid-strike legs (see
 * `isValidLeg`) the same way `computeNetPremium` does. */
export function payoffAtPrice(legs: OptionLeg[], price: number, netPremium: number): number {
  let total = netPremium;
  for (const leg of legs) {
    if (!isValidLeg(leg)) continue;
    const lotSize = toNum(leg.lot_size);
    const quantity = toNum(leg.quantity);
    total += legSign(leg) * intrinsicValue(leg, price) * lotSize * quantity;
  }
  return total;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Generates the exact expiry P&L curve for `legs`, on a grid shaped like
 * the backend's own `/payoff-curve` grid (0 up to 1.6x the highest
 * strike/spot, ~80 evenly spaced points, PLUS every leg's own strike
 * explicitly -- the true payoff function is piecewise-linear with kinks
 * only at strikes, so including them exactly keeps a sharp peak/trough
 * that sits on a strike from being understated by grid resolution, same
 * reasoning as the backend's own comment on this). Ignores unset/invalid-
 * strike legs entirely (see `isValidLeg`) -- both for the grid's own shape
 * and the payoff math. Returns `[]` when there are no VALID legs -- the
 * caller should fall back to the backend `curve` prop in that case
 * (nothing sane to derive locally). */
export function generateExpiryPayoffCurve(legs: OptionLeg[], liveSpot: number | null): LocalPayoffPoint[] {
  const realLegs = legs.filter(isValidLeg);
  if (realLegs.length === 0) return [];
  const strikes = realLegs.map((leg) => toNum(leg.strike));
  const spotNum = liveSpot != null ? toNum(liveSpot) : NaN;
  const spotForGrid = Number.isFinite(spotNum) && spotNum > 0 ? spotNum : Math.max(...strikes);
  const high = Math.max(...strikes, spotForGrid) * PAYOFF_GRID_UPPER_FACTOR;
  const low = 0;
  const step = high > low ? (high - low) / PAYOFF_GRID_POINTS : 1;

  const gridSet = new Set<number>(strikes.map(round2));
  for (let i = 0; i <= PAYOFF_GRID_POINTS; i++) {
    gridSet.add(round2(low + step * i));
  }
  const gridPrices = Array.from(gridSet).sort((a, b) => a - b);

  const netPremium = computeNetPremium(realLegs);
  return gridPrices.map((price) => ({
    underlying_price: price,
    expiry_pnl: round2(payoffAtPrice(realLegs, price, netPremium)),
  }));
}

/** Breakeven underlying price(s) for a locally-generated curve -- linear
 * interpolation between the two grid points bracketing each sign change
 * (and any point that lands exactly on zero). Used only while dragging, to
 * keep the chart's breakeven markers/x-axis labels moving live along with
 * the handle; the authoritative `RecalculatedMetricsRead.breakeven_points`
 * from the backend is used everywhere else (identical values once the
 * debounced refresh lands, since both use the same underlying formula). */
export function findLocalBreakevens(points: LocalPayoffPoint[]): number[] {
  // Normally fed `generateExpiryPayoffCurve`'s own output (already
  // guaranteed real numbers by that function's use of `toNum`/`round2`),
  // but `PayoffChart.tsx` falls back to the backend's raw `curve.points`
  // when there are no valid legs to generate a local curve from -- and
  // those, like everything else on the wire, can be Decimal-strings. `===
  // 0` and `+` (string concatenation between two strings) don't coerce,
  // unlike `< > - *`, so coerce explicitly here too rather than leave a
  // silent gap for the one caller that can hand this raw backend data.
  const prices = points.map((p) => toNum(p.underlying_price));
  const pnls = points.map((p) => toNum(p.expiry_pnl));

  const breakevens: number[] = [];
  for (let i = 0; i < points.length; i++) {
    if (pnls[i] === 0) breakevens.push(prices[i]);
    if (i === 0) continue;
    const aPnl = pnls[i - 1];
    const bPnl = pnls[i];
    if ((aPnl < 0 && bPnl > 0) || (aPnl > 0 && bPnl < 0)) {
      const t = -aPnl / (bPnl - aPnl);
      breakevens.push(round2(prices[i - 1] + t * (prices[i] - prices[i - 1])));
    }
  }
  return Array.from(new Set(breakevens)).sort((a, b) => a - b);
}

// ---- Strike interval inference + snapping ---------------------------------

/** Fallback exchange strike intervals for this platform's supported
 * underlyings, keyed by symbol substring (mirrors the seed values on the
 * backend's `Instrument.atm_strike_step`, which isn't exposed to the
 * frontend today) -- only used when the current legs don't already give us
 * enough information to infer it (see `getStrikeInterval` below). */
const KNOWN_STRIKE_INTERVALS: Record<string, number> = {
  BANKNIFTY: 100,
  MIDCPNIFTY: 25,
  FINNIFTY: 50,
  NIFTY: 50,
  SENSEX: 100,
  BANKEX: 100,
};

function gcd(a: number, b: number): number {
  let x = Math.round(Math.abs(a));
  let y = Math.round(Math.abs(b));
  while (y) {
    [x, y] = [y, x % y];
  }
  return x;
}

/** A leg fresh off `TradeBuilderCard`'s "Add Leg" button starts at
 * `strike: 0` before the trader fills it in, and clearing the strike input
 * to blank sets it to `Number("") === 0` too -- both are real, easily
 * reached states of `legs` while the trader is actively editing, not
 * hypothetical edge cases. Any strike-geometry calculation (wing width,
 * exchange interval, the chart's view window) that sorts ALL strikes and
 * measures gaps between adjacent ones treats a lingering `0` as a strike
 * 20,000+ points away from the real ones, producing an enormous "wing
 * width" -- and because this whole file's safety caps are deliberately
 * expressed as multiples of wing width (so they scale with the strategy's
 * own size), a corrupted wing width corrupts the cap meant to contain it
 * by the exact same factor, providing no protection at all. This is the
 * root cause of an axis that lands on the raw data grid's own bound
 * (`dataMax`) rather than a capped value: filtering out non-finite/
 * non-positive strikes before any geometry runs closes it at the source. */
function validStrikes(legs: OptionLeg[]): number[] {
  return Array.from(new Set(legs.filter(isValidLeg).map((leg) => toNum(leg.strike)))).sort((a, b) => a - b);
}

/** Best-effort exchange strike interval to snap drag handles to. Prefers
 * the GREATEST COMMON DIVISOR of the CURRENT legs' own strikes -- those are
 * always real, already-listed exchange strikes (from `synthesize()` or an
 * observed chain), so their GCD reliably reproduces the true interval
 * whenever at least two distinct strikes are present, with no dependency
 * on knowing the symbol at all. Falls back to a small lookup table for a
 * single-leg (or as-yet-unsynthesized) strategy, and finally to 50 if the
 * symbol isn't recognized either. Ignores unset/invalid strikes (see
 * `validStrikes`). */
export function getStrikeInterval(symbolCode: string, legs: OptionLeg[]): number {
  const uniqueStrikes = validStrikes(legs);
  if (uniqueStrikes.length >= 2) {
    let interval = uniqueStrikes[1] - uniqueStrikes[0];
    for (let i = 2; i < uniqueStrikes.length; i++) {
      interval = gcd(interval, uniqueStrikes[i] - uniqueStrikes[i - 1]);
    }
    if (interval > 0) return interval;
  }
  const upperSymbol = symbolCode.toUpperCase();
  for (const [key, step] of Object.entries(KNOWN_STRIKE_INTERVALS)) {
    if (upperSymbol.includes(key)) return step;
  }
  return 50;
}

export function snapToInterval(value: number, interval: number): number {
  if (!Number.isFinite(interval) || interval <= 0) return Math.round(value);
  return Math.round(value / interval) * interval;
}

// ---- Iron Condor leg identification + drag guardrails ---------------------

export type CondorRole = "LONG_PUT" | "SHORT_PUT" | "SHORT_CALL" | "LONG_CALL";

export interface CondorLegs {
  longPut: OptionLeg;
  shortPut: OptionLeg;
  shortCall: OptionLeg;
  longCall: OptionLeg;
}

export const CONDOR_ROLE_ORDER: CondorRole[] = ["LONG_PUT", "SHORT_PUT", "SHORT_CALL", "LONG_CALL"];

export const CONDOR_ROLE_LABELS: Record<CondorRole, string> = {
  LONG_PUT: "Long Put (Wing)",
  SHORT_PUT: "Short Put (Body)",
  SHORT_CALL: "Short Call (Body)",
  LONG_CALL: "Long Call (Wing)",
};

/** Recognizes a standard 4-leg Iron Condor shape: exactly one leg for each
 * of PE/BUY (long put wing), PE/SELL (short put body), CE/SELL (short call
 * body), CE/BUY (long call wing), all on the SAME expiry. Returns `null`
 * for every other leg shape (a different converted strategy, a
 * partially-edited condor missing a leg, duplicate/extra legs, or a
 * calendar/diagonal mix) -- draggable strike handles and the wing-width
 * controller are Iron-Condor-specific per this feature's scope ("primary
 * anchor strategy"); every other strategy keeps the existing, unmodified
 * read-only chart, completely unaffected. */
export function identifyCondorLegs(legs: OptionLeg[]): CondorLegs | null {
  if (legs.length !== 4) return null;
  if (new Set(legs.map((leg) => leg.expiry_date)).size !== 1) return null;

  const find = (optionType: "CE" | "PE", side: "BUY" | "SELL") =>
    legs.filter((leg) => leg.option_type === optionType && leg.side === side);

  const longPuts = find("PE", "BUY");
  const shortPuts = find("PE", "SELL");
  const shortCalls = find("CE", "SELL");
  const longCalls = find("CE", "BUY");

  if (longPuts.length !== 1 || shortPuts.length !== 1 || shortCalls.length !== 1 || longCalls.length !== 1) {
    return null;
  }
  return { longPut: longPuts[0], shortPut: shortPuts[0], shortCall: shortCalls[0], longCall: longCalls[0] };
}

function condorStrikesByRole(condor: CondorLegs): Record<CondorRole, number> {
  return {
    LONG_PUT: toNum(condor.longPut.strike),
    SHORT_PUT: toNum(condor.shortPut.strike),
    SHORT_CALL: toNum(condor.shortCall.strike),
    LONG_CALL: toNum(condor.longCall.strike),
  };
}

/** Structure Protection Guardrail: enforces
 * `Long Put < Short Put < Short Call < Long Call` (each separated by at
 * least one full strike interval) by clamping a candidate drag value
 * against its immediate neighbors' CURRENT strikes -- a cross-over attempt
 * (dragging the short put past the short call, say) simply stops moving at
 * the boundary rather than producing an invalid/overlapping structure.
 * Snaps to `interval` first, then clamps. */
export function applyCondorDragGuardrails(
  condor: CondorLegs,
  role: CondorRole,
  rawValue: number,
  interval: number
): number {
  const snapped = snapToInterval(rawValue, interval);
  const strikes = condorStrikesByRole(condor);
  const idx = CONDOR_ROLE_ORDER.indexOf(role);
  const lowerNeighbor = idx > 0 ? strikes[CONDOR_ROLE_ORDER[idx - 1]] : null;
  const upperNeighbor = idx < CONDOR_ROLE_ORDER.length - 1 ? strikes[CONDOR_ROLE_ORDER[idx + 1]] : null;
  const minAllowed = lowerNeighbor != null ? lowerNeighbor + interval : -Infinity;
  const maxAllowed = upperNeighbor != null ? upperNeighbor - interval : Infinity;
  return Math.min(Math.max(snapped, minAllowed), maxAllowed);
}

/** Applies a drag result for one condor leg onto the full `legs` array,
 * returning a NEW array (same leg objects except the one being dragged) --
 * ready to pass straight into `onChangeLegs`. `premium` is deliberately
 * left untouched here (still the pre-drag value) -- the backend's next
 * debounced `recalculate()`/`payoff-curve` response is what actually
 * re-prices the moved leg; this only moves the strike. */
export function applyCondorStrikeChange(legs: OptionLeg[], condor: CondorLegs, role: CondorRole, newStrike: number): OptionLeg[] {
  const targetLegId =
    role === "LONG_PUT"
      ? condor.longPut.leg_id
      : role === "SHORT_PUT"
        ? condor.shortPut.leg_id
        : role === "SHORT_CALL"
          ? condor.shortCall.leg_id
          : condor.longCall.leg_id;
  return legs.map((leg) => (leg.leg_id === targetLegId ? { ...leg, strike: newStrike } : leg));
}

/** Symmetric Wing Width Controller: widens/narrows BOTH protective wings by
 * the same number of points in one click -- the Long Put moves further
 * BELOW the Short Put, the Long Call moves further ABOVE the Short Call, by
 * `deltaPoints` each (a negative `deltaPoints` narrows both wings back in).
 * Body strikes (Short Put/Short Call) are never touched: the body defines
 * the credit received, the wings define the defined-risk cap, and
 * "symmetric wing width" means moving only the wings, equally on both
 * sides. Each wing is independently clamped so it can never cross its own
 * body (min one interval gap), and never negative. Returns a NEW legs
 * array ready for `onChangeLegs`. */
export function widenWingsSymmetrically(
  legs: OptionLeg[],
  condor: CondorLegs,
  deltaPoints: number,
  interval: number
): OptionLeg[] {
  // `-`/`Math.min`/`Math.max` coerce a numeric-string strike correctly, but
  // `+` does NOT -- if `strike` arrives as a Decimal-string (see `toNum`'s
  // own doc comment), `"23550" + 100` is the STRING "23550100", not the
  // number 23650, silently corrupting every "Wing width +/-" click. Coerce
  // explicitly rather than relying on which operator happens to be in use.
  const longPutStrike = toNum(condor.longPut.strike);
  const shortPutStrike = toNum(condor.shortPut.strike);
  const shortCallStrike = toNum(condor.shortCall.strike);
  const longCallStrike = toNum(condor.longCall.strike);
  const newLongPutStrike = Math.max(interval, Math.min(longPutStrike - deltaPoints, shortPutStrike - interval));
  const newLongCallStrike = Math.max(longCallStrike + deltaPoints, shortCallStrike + interval);
  return legs.map((leg) => {
    if (leg.leg_id === condor.longPut.leg_id) return { ...leg, strike: snapToInterval(newLongPutStrike, interval) };
    if (leg.leg_id === condor.longCall.leg_id) return { ...leg, strike: snapToInterval(newLongCallStrike, interval) };
    return leg;
  });
}

// ---- Chart view window (zoom) ----------------------------------------------

export interface ChartViewWindow {
  min: number;
  max: number;
}

/** The full data grid (`generateExpiryPayoffCurve`'s `[0, highestStrike *
 * 1.6]`) is deliberately wide so the curve's flat plateaus past the wings
 * are captured for exact P&L math -- but a Chart.js x-axis that auto-scales
 * to that full range squeezes every strike, both breakevens, and the
 * current-spot marker (all within a few hundred points of each other) into
 * a sliver a few pixels wide at one edge of the visible chart, with the
 * axis labels/markers overlapping into an unreadable smear. This computes
 * the actual VISIBLE x-axis window -- tight around where the trading
 * decision actually happens -- independent of the underlying data grid's
 * extent.
 *
 * CENTERED ON SPOT: the window is always `[center - halfWidth, center +
 * halfWidth]`, where `center` is the live/what-if underlying price -- a
 * trader reads a payoff chart relative to where the underlying actually
 * IS, so spot belongs in the middle, not wherever a strikes-only formula
 * happens to place it (an earlier version of this function anchored purely
 * to the strikes, which could leave spot sitting anywhere left-to-right
 * depending on where it fell relative to the wings). `halfWidth` is at
 * least `distance from center to the FARTHER extreme strike + 1.5 *
 * wingSpan` of padding, where `wingSpan` is the wider of the two wing gaps
 * (gap from an extreme strike to its nearest neighbor) -- for a standard
 * 23150/23250/23450/23550 Iron Condor with spot at 23346 this gives a
 * [22992, 23700] window. This also generalizes cleanly: a 2-strike
 * vertical spread's one gap serves as `wingSpan`; a single-strike leg (no
 * gap to measure) falls back to the exchange strike interval. A safety net
 * then widens (never narrows) `halfWidth` just enough to keep every
 * breakeven inside the window too, so a what-if move or an out-of-range
 * breakeven can't make its own marker silently vanish off-screen -- but
 * that widening, AND how far `center` itself may drift from the strikes'
 * own midpoint if `effectiveSpot` is itself an extreme/stale value, are
 * both CAPPED at an extra `MAX_SAFETY_WIDEN_WING_MULTIPLE` wing-widths (see
 * that constant). Without these caps, a single wildly out-of-range value
 * fed into `effectiveSpot`/`breakevens` -- whether a genuine extreme
 * what-if move, or (as happened in production) `metrics`/`breakevens` from
 * a just-replaced PREVIOUS strategy shape still being wired to freshly
 * "Converted" legs for the brief window before the debounced recalculate
 * lands -- would blow the whole window back toward the raw data grid's own
 * extent, the exact "unreadable sliver"/"axis blown out" failure this
 * function exists to prevent. A value beyond the cap simply renders
 * off-screen instead. */
export const MAX_SAFETY_WIDEN_WING_MULTIPLE = 2;

export function computeChartViewWindow(
  legs: OptionLeg[],
  effectiveSpot: number | null,
  breakevens: number[],
  strikeInterval: number,
  dataMin: number,
  dataMax: number
): ChartViewWindow {
  if (legs.length === 0) return { min: dataMin, max: dataMax };

  const strikes = validStrikes(legs);
  // `breakevens`/`effectiveSpot` can arrive as Decimal-strings too (the
  // backend's `RecalculatedMetricsRead.breakeven_points`/`spot` are the
  // same Python `Decimal` type as `OptionLeg.strike` -- see `toNum`'s own
  // doc comment), so coerce before checking finiteness the same way.
  const finiteBreakevens = breakevens.map(toNum).filter((b) => Number.isFinite(b));
  const spotNum = effectiveSpot != null ? toNum(effectiveSpot) : NaN;
  const spot = Number.isFinite(spotNum) ? spotNum : null;

  if (strikes.length === 0) {
    // Every leg is unset/invalid (e.g. a freshly "Added" leg with strike 0
    // not yet filled in, or ALL legs mid-edit) -- nothing real to anchor a
    // window to. Show a small default band around spot (or the data
    // range's own midpoint if there's no spot yet either) rather than
    // falling through to the full, unreadable raw data grid.
    const fallbackHalf = 5 * Math.max(strikeInterval, 1);
    const center = spot ?? (dataMin + dataMax) / 2;
    return {
      min: Math.max(dataMin, center - fallbackHalf),
      max: Math.min(dataMax, center + fallbackHalf),
    };
  }

  const lowest = strikes[0];
  const highest = strikes[strikes.length - 1];
  const strikesMid = (lowest + highest) / 2;

  const fallbackWingWidth = Math.max(strikeInterval, 1);
  const leftWingWidth = strikes.length >= 2 ? strikes[1] - strikes[0] : fallbackWingWidth;
  const rightWingWidth = strikes.length >= 2 ? strikes[strikes.length - 1] - strikes[strikes.length - 2] : fallbackWingWidth;
  const wingSpan = Math.max(leftWingWidth, rightWingWidth);
  const pad = 1.5 * wingSpan;
  const capExtra = MAX_SAFETY_WIDEN_WING_MULTIPLE * wingSpan;

  // Center on spot, but clamp how far that center may drift from the
  // strikes' own midpoint -- protects against `effectiveSpot` itself being
  // an extreme/stale value (the same class of bug `MAX_SAFETY_WIDEN_WING_
  // MULTIPLE` was introduced for) pulling the ENTIRE window off the
  // strikes and back toward the raw data grid. Falls back to the strikes'
  // own midpoint when there's no spot yet (e.g. before any price loads).
  const maxCenterDrift = pad + capExtra;
  const rawCenter = spot ?? strikesMid;
  const center = Math.max(strikesMid - maxCenterDrift, Math.min(strikesMid + maxCenterDrift, rawCenter));

  // Baseline half-width: far enough each way from center to show both
  // extreme strikes, plus 1.5 wing-widths of breathing room past whichever
  // strike sits farther from center.
  const strikeHalfSpan = Math.max(Math.abs(highest - center), Math.abs(lowest - center));
  let halfWidth = strikeHalfSpan + pad;

  // Widen (never narrow) so every breakeven stays visible too.
  for (const breakeven of finiteBreakevens) {
    const needed = Math.abs(breakeven - center) + pad;
    if (needed > halfWidth) halfWidth = needed;
  }

  // ABSOLUTE HARD CEILING: no matter how far a breakeven (a genuine
  // what-if move, or a stale value from a just-replaced strategy shape
  // still wired to fresh legs for the brief window before the debounced
  // recalculate lands) sits from center, the window widens at most
  // `MAX_SAFETY_WIDEN_WING_MULTIPLE` extra wing-widths past the strikes'
  // own half-span from center. A value beyond that renders off-screen
  // instead of blowing the axis back out toward the raw data grid -- and
  // combined with the `center` clamp above, the ENTIRE window is bounded
  // in wing-width multiples of the strikes themselves, with no path back
  // to `dataMin`/`dataMax` for any well-formed `legs` array.
  const maxHalfWidth = strikeHalfSpan + pad + capExtra;
  halfWidth = Math.min(halfWidth, maxHalfWidth);

  return {
    min: Math.max(dataMin, center - halfWidth),
    max: Math.min(dataMax, center + halfWidth),
  };
}

// ---- Static (read-only) strike grouping for non-Condor strategies --------

export interface StaticStrikeGroup {
  strike: number;
  /** BUY-leg blue / SELL-leg teal, matching the Condor handle convention;
   * gray when two-or-more legs share this exact strike (e.g. a calendar/
   * diagonal spread's near+far leg at the same strike) so the single
   * reference line doesn't misleadingly imply just one side. */
  color: string;
  /** e.g. "CE Buy", or "CE Sell + CE Buy" for a shared strike. */
  label: string;
}

const COLOR_STATIC_BUY = "#1976D2";
const COLOR_STATIC_SELL = "#00B386";
const COLOR_STATIC_SHARED = "#757575";

/** Groups `legs` by strike (rounded to cents, same convention as the payoff
 * grid) so every leg gets an on-chart reference line/label -- today only
 * the Iron-Condor-specific draggable handles do this; every other
 * supported strategy shape (13 others per this file's module docstring)
 * renders its payoff curve with NO visual tie back to which x-position is
 * which leg's strike, forcing the trader to cross-reference the Trade
 * Builder table above instead of reading the chart directly. */
export function buildStaticStrikeGroups(legs: OptionLeg[]): StaticStrikeGroup[] {
  const groups = new Map<number, OptionLeg[]>();
  for (const leg of legs) {
    const key = round2(toNum(leg.strike));
    groups.set(key, [...(groups.get(key) ?? []), leg]);
  }
  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([strike, legsAtStrike]) => {
      const color =
        legsAtStrike.length > 1
          ? COLOR_STATIC_SHARED
          : legsAtStrike[0].side === "BUY"
            ? COLOR_STATIC_BUY
            : COLOR_STATIC_SELL;
      const label = legsAtStrike
        .map((leg) => `${leg.option_type} ${leg.side === "BUY" ? "Buy" : "Sell"}`)
        .join(" + ");
      return { strike, color, label };
    });
}

// ---- Tooltip delta approximation ------------------------------------------

/** Local finite-difference slope of an already-fetched T+0 curve at one
 * grid index (`dP&L/dSpot` between its immediate neighbors) -- a
 * lightweight, honestly-labeled "Delta exposure" reading for the hover
 * tooltip. This is NOT a new Black-Scholes calculation, and NOT the same
 * figure as `RecalculatedMetricsRead.greeks.delta` (the aggregate delta at
 * ONE price, the current/what-if spot) -- it's that same quantity's shape
 * across the WHOLE curve, letting the tooltip show how delta itself
 * changes as the trader's mouse moves along the curve away from spot. */
export function approximateDeltaAt(points: Array<{ underlying_price: number; t0_pnl: number }>, index: number): number | null {
  if (points.length < 2 || index < 0 || index >= points.length) return null;
  const i0 = Math.max(0, index - 1);
  const i1 = Math.min(points.length - 1, index + 1);
  if (i0 === i1) return null;
  const dP = points[i1].t0_pnl - points[i0].t0_pnl;
  const dS = points[i1].underlying_price - points[i0].underlying_price;
  if (dS === 0) return null;
  return dP / dS;
}
